import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  FileText, Settings, Zap, Clock, CheckCircle, 
  ArrowLeft, ChevronRight, BookOpen, Target, Award,
  RefreshCw, Send, Download, Eye, AlertTriangle, Loader2
} from 'lucide-react';

const TEST_TYPES = [
  { id: 'quick', name: 'Quick Practice', questions: 5, marks: 10, duration: '5 min', icon: Zap },
  { id: 'unit', name: 'Unit Test', questions: 20, marks: 20, duration: '20 min', icon: FileText },
  { id: 'chapter', name: 'Chapter Mastery', questions: 30, marks: 30, duration: '30 min', icon: Target },
  { id: 'term', name: 'Term Test', questions: 40, marks: 40, duration: '45 min', icon: BookOpen },
  { id: 'annual', name: 'Annual Simulation', questions: 80, marks: 80, duration: '90 min', icon: Award }
];

const DIFFICULTY_LEVELS = [
  { id: 'easy', name: 'Easy', description: 'NCERT basics', color: 'emerald' },
  { id: 'medium', name: 'Medium', description: 'Standard level', color: 'amber' },
  { id: 'hard', name: 'Hard', description: 'Competitive prep', color: 'red' }
];

export default function ParentTestGenerator({ onNavigate }) {
  const { api, user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [generatedTests, setGeneratedTests] = useState([]);
  const [childClass, setChildClass] = useState('Class 10'); // Default
  
  // Form state
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [testType, setTestType] = useState('unit');
  const [difficulty, setDifficulty] = useState('medium');
  const [includeCompetitive, setIncludeCompetitive] = useState(false);
  const [variantCount, setVariantCount] = useState(3);

  // Fetch child's class on mount - with better error handling
  useEffect(() => {
    const fetchChildClass = async () => {
      try {
        const res = await api.get('/parent/dashboard');
        if (res.data?.children?.length > 0) {
          const child = res.data.children[0].child;
          setChildClass(child.class_name || 'Class 10');
        } else {
          // No linked child - use default class or user's class
          console.log('No linked child found, using default class');
        }
      } catch (err) {
        console.error('Failed to fetch child info:', err);
        // Continue with default class - don't block the feature
      }
    };
    fetchChildClass();
  }, [api]);

  // Fetch subjects
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const res = await api.get('/subjects');
        // Extract just the name from each subject object
        const subjectNames = res.data.map(s => typeof s === 'object' ? s.name : s);
        setSubjects(subjectNames);
      } catch (err) {
        console.error('Failed to fetch subjects:', err);
        // Fallback to default subjects
        setSubjects(['Math', 'Science', 'English', 'Social Science', 'Hindi', 'Physics', 'Chemistry', 'Biology']);
      }
    };
    fetchSubjects();
  }, [api]);

  // Fetch chapters when subject changes - USE CHILD'S CLASS
  useEffect(() => {
    if (selectedSubject) {
      const fetchChapters = async () => {
        try {
          const res = await api.get(`/chapters?subject=${selectedSubject}&class_name=${childClass}`);
          // Sort chapters by number (NCERT curriculum order)
          const sortedChapters = (res.data || []).sort((a, b) => a.number - b.number);
          setChapters(sortedChapters);
        } catch (err) {
          console.error('Failed to fetch chapters:', err);
        }
      };
      fetchChapters();
    }
  }, [api, selectedSubject, childClass]);

  const handleGenerateTests = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Generating test with params:', {
        subject: selectedSubject,
        chapters: selectedChapters,
        test_type: testType,
        difficulty: difficulty,
        include_competitive: includeCompetitive,
        variant_count: variantCount
      });
      
      const res = await api.post('/parent/generate-test', {
        subject: selectedSubject,
        chapters: selectedChapters,
        test_type: testType,
        difficulty: difficulty,
        include_competitive: includeCompetitive,
        variant_count: variantCount
      });
      
      console.log('Test generation response:', res.data);
      
      if (res.data.variants && res.data.variants.length > 0) {
        setGeneratedTests(res.data.variants);
        setStep(4);
      } else {
        setError('No tests were generated. Please try again.');
      }
    } catch (err) {
      console.error('Failed to generate tests:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to generate tests. Please try again.';
      setError(errorMessage);
    }
    setLoading(false);
  };

  const handleSendChallenge = async (testId) => {
    if (!user?.linked_child_id && !user?.linked_children?.length) {
      setError('No linked child account. Please link a child account first from your profile.');
      return;
    }
    
    try {
      const childId = user?.linked_child_id || user?.linked_children?.[0];
      const formData = new FormData();
      formData.append('test_id', testId);
      formData.append('child_id', childId);
      
      const res = await api.post('/parent/send-challenge', formData);
      if (res.data.status === 'sent') {
        alert('Challenge sent to your child! They will see it when they log in.');
      }
    } catch (err) {
      console.error('Failed to send challenge:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to send challenge.';
      setError(errorMessage);
    }
  };

  const handleDownloadTest = async (testId) => {
    try {
      const res = await api.get(`/parent/tests/${testId}`);
      const test = res.data;
      
      // Generate printable HTML
      const questionsHtml = test.questions?.map((q, i) => `
        <div style="margin-bottom: 20px; page-break-inside: avoid;">
          <p><strong>Q${i + 1}.</strong> ${q.question}</p>
          ${q.options ? `
            <div style="margin-left: 20px;">
              ${q.options.map((opt, j) => `<p>${String.fromCharCode(65 + j)}. ${opt}</p>`).join('')}
            </div>
          ` : ''}
        </div>
      `).join('') || '';

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${test.subject || selectedSubject} Test</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
            h1 { color: #333; border-bottom: 2px solid #7c3aed; padding-bottom: 10px; }
          </style>
        </head>
        <body>
          <h1>${test.subject || selectedSubject} Test</h1>
          <p>Questions: ${test.questions?.length || 0} | Difficulty: ${test.difficulty || difficulty}</p>
          <hr style="margin: 20px 0;">
          ${questionsHtml}
        </body>
        </html>
      `;

      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${test.subject || selectedSubject}_Test.html`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download:', err);
      setError('Failed to download test. Please try again.');
    }
  };

  const selectedTestType = TEST_TYPES.find(t => t.id === testType);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="min-h-screen bg-[#0f172a] pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0f172a]/95 backdrop-blur-sm border-b border-slate-800/50 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => step > 1 ? setStep(step - 1) : onNavigate('parent-dashboard')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white font-['Outfit']">Create Test</h1>
            <p className="text-xs text-slate-500">Step {step} of 4</p>
          </div>
          {/* Progress */}
          <div className="flex gap-1">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className={`w-2 h-2 rounded-full ${s <= step ? 'bg-violet-500' : 'bg-slate-700'}`} />
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-6">
        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-300">{error}</p>
          </motion.div>
        )}

        {/* Step 1: Select Subject & Chapters */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-xl font-bold text-white mb-2">Select Subject</h2>
              <p className="text-slate-400 text-sm">Choose a subject for the test</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {subjects.map(subject => (
                <button
                  key={subject}
                  onClick={() => {
                    setSelectedSubject(subject);
                    setSelectedChapters([]);
                  }}
                  className={`p-4 rounded-xl border text-left transition-all ${
                    selectedSubject === subject
                      ? 'bg-violet-600/20 border-violet-500'
                      : 'bg-slate-900/50 border-slate-800 hover:border-violet-500/50'
                  }`}
                >
                  <span className="text-2xl mb-2 block">
                    {subject === 'Math' ? '📐' : subject === 'Science' ? '🧬' : subject === 'English' ? '📖' : '📚'}
                  </span>
                  <span className="text-white font-medium">{subject}</span>
                </button>
              ))}
            </div>

            {selectedSubject && chapters.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-white mb-3">Select Chapters</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {chapters.map(chapter => (
                    <button
                      key={chapter.number}
                      onClick={() => {
                        setSelectedChapters(prev => 
                          prev.includes(chapter.number)
                            ? prev.filter(c => c !== chapter.number)
                            : [...prev, chapter.number]
                        );
                      }}
                      className={`w-full p-3 rounded-xl border text-left transition-all flex items-center justify-between ${
                        selectedChapters.includes(chapter.number)
                          ? 'bg-violet-600/20 border-violet-500'
                          : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                      }`}
                    >
                      <span className="text-white">Ch {chapter.number}: {chapter.name}</span>
                      {selectedChapters.includes(chapter.number) && (
                        <CheckCircle className="w-5 h-5 text-violet-400" />
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">{selectedChapters.length} chapter(s) selected</p>
              </div>
            )}

            <button
              onClick={() => setStep(2)}
              disabled={!selectedSubject || selectedChapters.length === 0}
              className="w-full bg-violet-600 hover:bg-violet-700 disabled:bg-slate-800 disabled:text-slate-500 text-white rounded-xl h-14 font-semibold flex items-center justify-center gap-2 transition-all"
            >
              Continue <ChevronRight className="w-5 h-5" />
            </button>
          </motion.div>
        )}

        {/* Step 2: Select Test Type */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-xl font-bold text-white mb-2">Test Type</h2>
              <p className="text-slate-400 text-sm">Choose the format of the test</p>
            </div>

            <div className="space-y-3">
              {TEST_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setTestType(type.id)}
                    className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-4 ${
                      testType === type.id
                        ? 'bg-violet-600/20 border-violet-500'
                        : 'bg-slate-900/50 border-slate-800 hover:border-violet-500/50'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl ${testType === type.id ? 'bg-violet-500/30' : 'bg-slate-800'} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${testType === type.id ? 'text-violet-400' : 'text-slate-400'}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{type.name}</p>
                      <p className="text-xs text-slate-400">{type.questions} questions • {type.marks} marks • {type.duration}</p>
                    </div>
                    {testType === type.id && <CheckCircle className="w-5 h-5 text-violet-400" />}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setStep(3)}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-xl h-14 font-semibold flex items-center justify-center gap-2 transition-all"
            >
              Continue <ChevronRight className="w-5 h-5" />
            </button>
          </motion.div>
        )}

        {/* Step 3: Settings */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-xl font-bold text-white mb-2">Test Settings</h2>
              <p className="text-slate-400 text-sm">Customize difficulty and options</p>
            </div>

            {/* Difficulty */}
            <div>
              <h3 className="text-sm font-medium text-slate-400 mb-3">Difficulty Level</h3>
              <div className="grid grid-cols-3 gap-2">
                {DIFFICULTY_LEVELS.map(level => (
                  <button
                    key={level.id}
                    onClick={() => setDifficulty(level.id)}
                    className={`p-3 rounded-xl border text-center transition-all ${
                      difficulty === level.id
                        ? `bg-${level.color}-500/20 border-${level.color}-500`
                        : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <p className={`font-medium ${difficulty === level.id ? `text-${level.color}-400` : 'text-white'}`}>
                      {level.name}
                    </p>
                    <p className="text-xs text-slate-500">{level.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Competitive Questions Toggle */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Include Competitive Questions</p>
                  <p className="text-xs text-slate-400">Add Olympiad-level questions (Class 10-12)</p>
                </div>
                <button
                  onClick={() => setIncludeCompetitive(!includeCompetitive)}
                  className={`w-12 h-7 rounded-full transition-all ${
                    includeCompetitive ? 'bg-violet-600' : 'bg-slate-700'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-all ${
                    includeCompetitive ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>

            {/* Variant Count */}
            <div>
              <h3 className="text-sm font-medium text-slate-400 mb-3">Number of Variants</h3>
              <div className="flex gap-2">
                {[1, 3, 5].map(count => (
                  <button
                    key={count}
                    onClick={() => setVariantCount(count)}
                    className={`flex-1 p-3 rounded-xl border text-center transition-all ${
                      variantCount === count
                        ? 'bg-cyan-500/20 border-cyan-500'
                        : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <p className={`font-bold ${variantCount === count ? 'text-cyan-400' : 'text-white'}`}>{count}</p>
                    <p className="text-xs text-slate-500">variant{count > 1 ? 's' : ''}</p>
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">Each variant has different questions</p>
            </div>

            {/* Summary */}
            <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-4">
              <h3 className="text-sm font-medium text-violet-300 mb-2">Test Summary</h3>
              <div className="space-y-1 text-sm">
                <p className="text-slate-300">📚 {selectedSubject} • {selectedChapters.length} chapter(s)</p>
                <p className="text-slate-300">📝 {selectedTestType?.name} • {selectedTestType?.questions} questions</p>
                <p className="text-slate-300">⏱️ Duration: {selectedTestType?.duration}</p>
                <p className="text-slate-300">🎯 Difficulty: {DIFFICULTY_LEVELS.find(d => d.id === difficulty)?.name}</p>
                <p className="text-slate-300">📄 {variantCount} variant(s) will be generated</p>
              </div>
            </div>

            <button
              onClick={handleGenerateTests}
              disabled={loading}
              className="w-full bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 disabled:opacity-50 text-white rounded-xl h-14 font-semibold flex items-center justify-center gap-2 transition-all"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Generating...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" /> Generate Tests
                </>
              )}
            </button>
          </motion.div>
        )}

        {/* Step 4: Generated Tests */}
        {step === 4 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Tests Generated!</h2>
              <p className="text-slate-400 text-sm">{generatedTests.length} unique variants ready</p>
            </div>

            <div className="space-y-3">
              {generatedTests.map((test, i) => (
                <div
                  key={test.id || i}
                  className="bg-slate-900/50 border border-slate-800 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-white font-medium">Variant {i + 1}</p>
                      <p className="text-xs text-slate-400">{test.question_count || selectedTestType?.questions} questions</p>
                    </div>
                    <span className="text-xs bg-violet-500/20 text-violet-400 px-2 py-1 rounded-full">
                      {selectedTestType?.name}
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => onNavigate('test-preview', { testId: test.id })}
                      className="flex-1 bg-slate-800 hover:bg-slate-700 text-white rounded-lg py-2 text-sm flex items-center justify-center gap-1"
                    >
                      <Eye className="w-4 h-4" /> Preview
                    </button>
                    <button
                      onClick={() => handleSendChallenge(test.id)}
                      className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-lg py-2 text-sm flex items-center justify-center gap-1"
                    >
                      <Send className="w-4 h-4" /> Send
                    </button>
                    <button
                      onClick={() => handleDownloadTest(test.id)}
                      className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg px-3 py-2 text-sm flex items-center justify-center"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => {
                setStep(1);
                setGeneratedTests([]);
                setSelectedSubject(null);
                setSelectedChapters([]);
              }}
              className="w-full bg-slate-800 hover:bg-slate-700 text-white rounded-xl h-12 font-medium flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Create Another Test
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
